import { clamp, fontStack, normalizeText, textEditorWidth, textItemId } from './utils.js'

export const annotationMethods = {
  renderAnnotations(pageState) {
    for (const [stackIndex, annotation] of pageState.annotations.entries()) {
      const isSelected = annotation.id === this.selectedAnnotationId
      const isEditable = annotation.editable !== false
      const isEditing = isSelected && isEditable && this.editingAnnotationId === annotation.id
      const note = document.createElement(isEditing ? 'textarea' : 'button') as HTMLTextAreaElement | HTMLButtonElement
      note.className = 'annotation-note'
      note.dataset.annotationId = annotation.id
      note.style.zIndex = String(stackIndex + 1)
      if (isEditing) {
        note.classList.add('annotation-editor')
        note.value = annotation.text
        note.setAttribute('aria-label', 'Edit inserted text')
      } else {
        if (note instanceof HTMLButtonElement) note.type = 'button'
        note.textContent = annotation.text
        note.setAttribute('aria-label', `${isEditable ? 'Edit' : 'Select'} inserted text: ${annotation.text}`)
      }
      note.classList.toggle('selected', isSelected)
      note.classList.toggle('locked', !isEditable)
      note.style.left = `${annotation.x * 100}%`
      note.style.top = `${annotation.y * 100}%`
      note.style.fontSize = `${annotation.size * this.zoom}px`
      note.style.fontFamily = fontStack(annotation.fontFamily)
      note.style.color = annotation.color
      if (isEditing) {
        note.style.width = `${textEditorWidth(annotation.text, annotation.size * this.zoom, annotation.fontFamily)}px`
        note.style.height = `${annotation.size * this.zoom + 6}px`
      }
      note.addEventListener('pointerdown', event => this.startAnnotationMove(event, annotation))
      note.addEventListener('click', event => {
        event.stopPropagation()
        if (annotation.id !== this.selectedAnnotationId && note.dataset.moved !== 'true') this.selectAnnotation(annotation.id)
        note.dataset.moved = 'false'
      })
      note.addEventListener('dblclick', event => {
        event.preventDefault()
        event.stopPropagation()
        if (isEditable) this.startAnnotationEdit(annotation.id)
      })
      note.addEventListener('keydown', event => {
        const keyboardEvent = event as KeyboardEvent
        if (keyboardEvent.key.startsWith('Arrow') && !isEditing) {
          event.preventDefault()
          event.stopPropagation()
          if (annotation.id !== this.selectedAnnotationId) this.selectAnnotation(annotation.id)
          else this.nudgeSelectedAnnotation(keyboardEvent.key, keyboardEvent.shiftKey)
        }
      })
      if (isEditing) this.bindAnnotationEditor(note, annotation)
      this.annotationLayer.append(note)
    }
  },

  bindAnnotationEditor(editor, annotation) {
    let closingEditor = false
    const historySnapshot = this.captureHistoryState()
    let historyRecorded = false
    const update = () => {
      if (!editor.isConnected) return
      const text = normalizeText(editor.value)
      if (text !== annotation.text && !historyRecorded) {
        this.recordHistorySnapshot(historySnapshot)
        historyRecorded = true
      }
      annotation.text = text
      editor.style.width = `${textEditorWidth(annotation.text, annotation.size * this.zoom, annotation.fontFamily)}px`
      this.updateDirtyState()
    }

    editor.addEventListener('input', update)
    editor.addEventListener('keyup', update)
    editor.addEventListener('keydown', event => {
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        closingEditor = true
        update()
        this.exitAnnotationEdit()
      }
    })
    editor.addEventListener('blur', () => {
      if (closingEditor) return
      update()
      this.editingAnnotationId = null
      this.renderCurrentPage()
    })
    if (this.focusAnnotationEditor) {
      this.focusAnnotationEditor = false
      requestAnimationFrame(() => editor.focus())
    }
  },

  ensureTextInsertPreview() {
    if (this.textInsertPreview?.isConnected) return this.textInsertPreview

    this.textInsertPreview = document.createElement('div')
    this.textInsertPreview.className = 'annotation-note annotation-preview'
    this.textInsertPreview.setAttribute('aria-hidden', 'true')
    this.annotationLayer.append(this.textInsertPreview)
    return this.textInsertPreview
  },

  ensureAnnotationToolPanel() {
    if (this.annotationToolPanel?.isConnected) return this.annotationToolPanel

    const panel = document.createElement('div')
    panel.className = 'annotation-tool-panel'
    panel.hidden = true
    panel.innerHTML = `
      <label class="annotation-tool-field">
        <span>Size</span>
        <input class="form-control annotation-size" type="number" min="8" max="144" value="24" aria-label="Selected text size" />
      </label>
      <label class="annotation-tool-field">
        <span>Color</span>
        <input class="form-control form-control-color annotation-color" type="color" value="#333333" aria-label="Selected text color" />
      </label>
    `
    panel.addEventListener('pointerdown', event => event.stopPropagation())
    panel.addEventListener('click', event => event.stopPropagation())
    const sizeInput = panel.querySelector<HTMLInputElement>('.annotation-size')
    const colorInput = panel.querySelector<HTMLInputElement>('.annotation-color')
    const bindStyleHistory = (input, update) => {
      if (!input) return
      let snapshot = null
      let recorded = false
      const begin = () => {
        snapshot = this.captureHistoryState()
        recorded = false
      }
      input.addEventListener('focus', begin)
      input.addEventListener('input', event => {
        if (!snapshot) begin()
        if (!recorded) {
          this.recordHistorySnapshot(snapshot)
          recorded = true
        }
        update(event.currentTarget)
      })
      input.addEventListener('blur', () => { snapshot = null })
    }
    bindStyleHistory(sizeInput, input => this.updateSelectedTextStyle({ size: input.valueAsNumber }))
    bindStyleHistory(colorInput, input => this.updateSelectedTextStyle({ color: input.value }))
    this.sheet.append(panel)
    this.annotationToolPanel = panel
    return panel
  },

  textInsertOptions() {
    return {
      text: this.textInsertValue || '',
      size: 24,
      color: '#333333',
    }
  },

  createAnnotationId(pageState) {
    const nextIndex = pageState.annotations.length + 1
    return `${pageState.id}:annotation:${Date.now()}:${nextIndex}`
  },

  insertedTextStyleFor(pageState, x, y, fallbackSize = 24) {
    const candidates = pageState.textItems.filter(item => item.fontFamily || item.fontHeight)
    const fallback = {
      size: fallbackSize,
      fontFamily: '',
    }
    if (!candidates.length) return fallback

    const nearest = candidates.reduce((best, item) => {
      const centerX = item.x + item.width / 2
      const centerY = item.y + item.height / 2
      const edgeX = x < item.x ? item.x - x : Math.max(0, x - (item.x + item.width))
      const lineDistance = Math.abs(centerY - y)
      const distance = Math.hypot(edgeX, lineDistance * 2.25, centerX - x)
      const sameLine = y >= item.y - item.height * 0.65 && y <= item.y + item.height * 1.65
      const score = distance + (sameLine ? 0 : item.height * 4)
      return !best || score < best.score ? { item, score } : best
    }, null)

    const layerRect = this.annotationLayer.getBoundingClientRect()
    const matchedSize = nearest?.item.fontHeight && layerRect.height && this.zoom
      ? nearest.item.fontHeight * layerRect.height / this.zoom
      : fallbackSize

    return {
      size: clamp(matchedSize, 8, 144),
      fontFamily: nearest?.item.fontFamily || candidates[0].fontFamily || '',
    }
  },

  fontFamilyForInsertedText(pageState, x, y) {
    return this.insertedTextStyleFor(pageState, x, y).fontFamily
  },

  updateTextInsertPreview(event = null) {
    if (!this.textMode || this.selectedAnnotationId || !this.visiblePages[this.currentIndex]) {
      this.hideTextInsertPreview()
      return
    }

    const { text, size, color } = this.textInsertOptions()
    if (!text) {
      this.hideTextInsertPreview()
      return
    }

    const preview = this.ensureTextInsertPreview()
    preview.classList.toggle('locked', this.markMode)
    const rect = this.annotationLayer.getBoundingClientRect()
    if (event && rect.width && rect.height) {
      this.textInsertPreviewPosition = {
        x: clamp((event.clientX - rect.left) / rect.width, 0, 1),
        y: clamp((event.clientY - rect.top) / rect.height, 0, 1),
      }
    }

    if (!this.textInsertPreviewPosition) {
      this.hideTextInsertPreview()
      return
    }

    const matchedStyle = this.insertedTextStyleFor(this.visiblePages[this.currentIndex], this.textInsertPreviewPosition.x, this.textInsertPreviewPosition.y, size)

    preview.textContent = text
    preview.style.left = `${this.textInsertPreviewPosition.x * 100}%`
    preview.style.top = `${this.textInsertPreviewPosition.y * 100}%`
    preview.style.fontSize = `${matchedStyle.size * this.zoom}px`
    preview.style.fontFamily = fontStack(matchedStyle.fontFamily)
    preview.style.color = color
    preview.hidden = false
  },

  hideTextInsertPreview() {
    this.textInsertPreviewPosition = null
    if (this.textInsertPreview) this.textInsertPreview.hidden = true
  },

  startAnnotationMove(event, annotation) {
    if (event.button !== 0 || event.target.closest('.annotation-editor')) return
    const rect = this.annotationLayer.getBoundingClientRect()
    if (!rect.width || !rect.height) return

    event.stopPropagation()
    event.currentTarget.dataset.moved = 'false'
    this.annotationDrag = {
      annotation,
      element: event.currentTarget,
      historySnapshot: this.captureHistoryState(),
      moved: false,
      pointerOffsetX: (event.clientX - rect.left) / rect.width - annotation.x,
      pointerOffsetY: (event.clientY - rect.top) / rect.height - annotation.y,
    }
  },

  moveAnnotation(event) {
    if (!this.annotationDrag) return
    const rect = this.annotationLayer.getBoundingClientRect()
    if (!rect.width || !rect.height) return

    const x = clamp((event.clientX - rect.left) / rect.width - this.annotationDrag.pointerOffsetX, 0, 1)
    const y = clamp((event.clientY - rect.top) / rect.height - this.annotationDrag.pointerOffsetY, 0, 1)
    this.annotationDrag.annotation.x = x
    this.annotationDrag.annotation.y = y
    this.annotationDrag.element.style.left = `${x * 100}%`
    this.annotationDrag.element.style.top = `${y * 100}%`
    this.annotationDrag.moved = true
    this.annotationDrag.element.dataset.moved = 'true'
    this.positionAnnotationToolPanel()
  },

  endAnnotationMove() {
    if (!this.annotationDrag) return
    if (this.annotationDrag.moved) {
      this.recordHistorySnapshot(this.annotationDrag.historySnapshot)
      this.updateDirtyState()
      this.setStatus('Inserted text moved.')
    }
    this.annotationDrag = null
  },

  selectedAnnotation() {
    const pageState = this.visiblePages[this.currentIndex]
    return pageState?.annotations.find(annotation => annotation.id === this.selectedAnnotationId) || null
  },

  syncAnnotationTools() {
    const annotation = this.selectedAnnotation()
    const pdfText = this.selectedTextItem
    const selected = annotation || pdfText
    const panel = this.ensureAnnotationToolPanel()

    panel.hidden = !selected
    const sizeInput = panel.querySelector('.annotation-size')
    const colorInput = panel.querySelector('.annotation-color')
    if (sizeInput) {
      sizeInput.disabled = !selected
      if (annotation) sizeInput.value = String(Math.round(annotation.size))
      if (pdfText) sizeInput.value = String(Math.round(this.pdfTextSizeInPoints(pdfText)))
    }
    if (colorInput) {
      colorInput.disabled = !selected
      if (annotation) colorInput.value = annotation.color || '#333333'
      if (pdfText) colorInput.value = pdfText.textColor || '#000000'
    }
    this.positionAnnotationToolPanel()
  },

  positionAnnotationToolPanel() {
    const panel = this.annotationToolPanel
    if (!panel || panel.hidden) return

    const selector = this.selectedAnnotationId
      ? `[data-annotation-id="${CSS.escape(this.selectedAnnotationId)}"]`
      : this.selectedTextItem
        ? `[data-text-id="${CSS.escape(textItemId(this.selectedTextItem))}"]`
        : ''
    const note = selector && this.sheet.querySelector(selector)
    if (!note) {
      panel.hidden = true
      return
    }

    const layerRect = this.annotationLayer.getBoundingClientRect()
    const noteRect = note.getBoundingClientRect()
    const left = clamp(noteRect.left - layerRect.left + noteRect.width / 2, 0, layerRect.width)
    const top = clamp(noteRect.top - layerRect.top - 10, 0, layerRect.height)
    panel.style.left = `${left}px`
    panel.style.top = `${top}px`
  },

  updateSelectedTextStyle({ size, color }) {
    const annotation = this.selectedAnnotation()
    if (annotation) {
      this.updateSelectedAnnotationStyle({ size, color })
      return
    }
    this.updateSelectedPdfTextStyle({ size, color })
  },

  updateSelectedAnnotationStyle({ size, color }) {
    const annotation = this.selectedAnnotation()
    if (!annotation) return

    if (Number.isFinite(size)) annotation.size = clamp(size, 8, 144)
    if (color) annotation.color = color

    const note = this.annotationLayer.querySelector(`[data-annotation-id="${CSS.escape(annotation.id)}"]`)
    if (note) {
      if (note instanceof HTMLTextAreaElement && note !== document.activeElement) note.value = annotation.text
      if (note instanceof HTMLButtonElement) note.textContent = annotation.text
      note.style.fontSize = `${annotation.size * this.zoom}px`
      note.style.color = annotation.color
      if (note.classList.contains('annotation-editor')) {
        note.style.width = `${textEditorWidth(annotation.text, annotation.size * this.zoom, annotation.fontFamily)}px`
        note.style.height = `${annotation.size * this.zoom + 6}px`
      }
    }
    this.syncAnnotationTools()
    this.updateDirtyState()
  },

  pdfTextSizeInPoints(item) {
    const rect = this.textEditLayer.getBoundingClientRect()
    if (!rect.height || !this.zoom) return 8
    return Math.max(8, (item.fontHeight || item.height) * rect.height / this.zoom)
  },

  updateSelectedPdfTextStyle({ size, color }) {
    const pageState = this.visiblePages[this.currentIndex]
    const selected = this.selectedTextItem
    if (!pageState || !selected) return
    const id = textItemId(selected)
    const item = pageState.textItems.find(candidate => textItemId(candidate) === id)
    if (!item) return

    const existing = pageState.textEdits[id]
    const overrides: any = {}
    if (Number.isFinite(size)) {
      const points = clamp(size, 8, 144)
      const rect = this.textEditLayer.getBoundingClientRect()
      if (rect.height && this.zoom) {
        const fontHeight = points * this.zoom / rect.height
        const currentFontHeight = existing?.fontHeight || item.fontHeight || item.height
        const currentHeight = existing?.height || item.height
        overrides.fontHeight = fontHeight
        overrides.height = clamp(currentHeight * fontHeight / currentFontHeight, 0.01, 1 - (existing?.y ?? item.y))
      }
    }
    if (color) overrides.textColor = color

    const text = existing?.text ?? item.text
    const edit = this.autoSizeTextEdit(this.buildTextEdit(item, text, overrides), item)
    pageState.textEdits[id] = edit
    this.selectedTextItem = this.textItemWithEdit(item, edit)
    this.updateDirtyState()
    clearTimeout(this.textStyleRenderTimer)
    this.textStyleRenderTimer = setTimeout(() => this.renderCurrentPage(), 50)
  },

  shouldHandleAnnotationNudge(event) {
    if (!this.selectedAnnotationId || !event.key.startsWith('Arrow')) return false
    const target = event.target
    if (!target?.closest) return true
    if (target.closest('.annotation-tool-panel')) return false
    if (target.closest('textarea, input, select, [contenteditable="true"]')) return event.altKey
    return true
  },

  nudgeSelectedAnnotation(key, largeStep = false) {
    const annotation = this.selectedAnnotation()
    const rect = this.annotationLayer.getBoundingClientRect()
    if (!annotation || !rect.width || !rect.height) return

    const points = largeStep ? 10 : 1
    const dx = points * this.zoom / rect.width
    const dy = points * this.zoom / rect.height
    const movement = {
      ArrowLeft: [-dx, 0],
      ArrowRight: [dx, 0],
      ArrowUp: [0, -dy],
      ArrowDown: [0, dy],
    }[key]
    if (!movement) return

    const x = clamp(annotation.x + movement[0], 0, 1)
    const y = clamp(annotation.y + movement[1], 0, 1)
    if (x === annotation.x && y === annotation.y) return
    this.recordHistory()
    annotation.x = x
    annotation.y = y
    this.renderCurrentPage()
    this.setStatus(`Inserted text moved ${largeStep ? '10 points' : '1 point'}.`)
  },

  selectAnnotation(id) {
    this.editingTextItemId = null
    this.selectedTextItem = null
    this.editingAnnotationId = null
    this.selectedAnnotationId = id
    document.querySelectorAll('.editable-text.selected').forEach(control => control.classList.remove('selected'))
    document.querySelectorAll('.annotation-note').forEach(note =>
      note.classList.toggle('selected', note.getAttribute('data-annotation-id') === id))
    this.syncAnnotationTools()
    this.updateDeleteControl()
    this.setStatus('Inserted text selected. Double-click to edit its content.')
  },

  startAnnotationEdit(id) {
    const pageState = this.visiblePages[this.currentIndex]
    const annotation = pageState?.annotations.find(item => item.id === id)
    if (!annotation || annotation.editable === false) return
    this.selectAnnotation(id)
    this.editingAnnotationId = id
    this.focusAnnotationEditor = true
    this.renderCurrentPage()
  },

  removeAnnotation(id) {
    const pageState = this.visiblePages[this.currentIndex]
    if (!pageState) return false
    const index = pageState.annotations.findIndex(annotation => annotation.id === id)
    if (index === -1) return false

    this.recordHistory()
    pageState.annotations.splice(index, 1)
    if (this.selectedAnnotationId === id) {
      this.selectedAnnotationId = null
      this.editingAnnotationId = null
      this.focusAnnotationEditor = false
      this.syncAnnotationTools()
    }
    this.updateDirtyState()
    this.renderCurrentPage()
    this.setStatus('Inserted text removed.')
    return true
  },

  exitAnnotationEdit() {
    if (!this.editingAnnotationId) return
    const pageState = this.visiblePages[this.currentIndex]
    const annotation = pageState?.annotations.find(item => item.id === this.selectedAnnotationId)
    if (annotation?.editable !== false && !annotation.text.trim() && this.removeAnnotation(annotation.id)) return
    this.editingAnnotationId = null
    this.focusAnnotationEditor = false
    this.syncAnnotationTools()
    this.renderCurrentPage()
    this.setStatus('Inserted text edit closed. Double-click to edit again.')
  },

  deselectAnnotation() {
    if (!this.selectedAnnotationId) return
    this.editingAnnotationId = null
    this.selectedAnnotationId = null
    this.focusAnnotationEditor = false
    document.querySelectorAll('.annotation-note.selected').forEach(note => note.classList.remove('selected'))
    this.syncAnnotationTools()
    this.updateDeleteControl()
  }
}
